import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, X, RefreshCw, Zap, ZapOff, CheckCircle2, AlertTriangle, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { poseDetector } from "@/lib/poseDetector";

/**
 * Guided camera capture for full-body posture photos.
 * - getUserMedia (rear-cam preferred on tablet/phone)
 * - Animated silhouette + plumb line + horizontal guide lines
 * - Real-time heuristics: centering, tilt, distance, lighting, sharpness
 * - Optional auto-capture when the body holds alignment for N frames
 * - Quality check before returning the image
 */

const NEON = {
  blue:  { stroke: "rgba(56,189,248,0.85)",  glow: "rgba(56,189,248,0.55)" },
  green: { stroke: "rgba(52,211,153,0.85)",  glow: "rgba(52,211,153,0.55)" },
  amber: { stroke: "rgba(251,191,36,0.88)",  glow: "rgba(251,191,36,0.55)" },
  red:   { stroke: "rgba(244,63,94,0.9)",    glow: "rgba(244,63,94,0.55)" },
};

function aggregateSeverity(g) {
  const issues = [];
  if (g.lighting !== "ok") issues.push("warning");
  if (g.sharpness === "blurry") issues.push("warning");
  if (g.centered === false) issues.push("major");
  if (g.tilt !== "ok") issues.push("warning");
  if (g.distance !== "ok") issues.push("major");
  if (!issues.length) return "green";
  if (issues.includes("major")) return "red";
  return "amber";
}

function isAligned(g) {
  return (
    g.centered === true &&
    g.tilt === "ok" &&
    g.distance === "ok" &&
    g.lighting === "ok" &&
    g.sharpness === "ok"
  );
}

export default function CameraCapture({ view = "front", gender = "neutro", onCapture, onCancel }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const analyzeCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const autoRef = useRef({ goodFrames: 0, capturing: false });
  const [wrapDims, setWrapDims] = useState({ w: 0, h: 0 });
  const [smallRoom, setSmallRoom] = useState(false);
  const [state, setState] = useState({
    ready: false,
    error: null,
    auto: false,
    hint: "A iniciar câmara…",
    severity: "blue",
    guidance: {
      centered: null,
      tilt: "ok",
      distance: "ok",
      lighting: "ok",
      sharpness: "ok",
    },
  });

  // Try to start camera
  useEffect(() => {
    let mounted = true;
    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Este dispositivo não suporta acesso à câmara.");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: view === "front" ? "environment" : "environment" },
            width:  { ideal: 1280 },
            height: { ideal: 1920 },
          },
          audio: false,
        });
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute("playsinline", "true");
          videoRef.current.muted = true;
          await videoRef.current.play().catch(() => {});
        }
        // Attempt to load pose detector (no-op today)
        poseDetector.load().catch(() => {});
        setState(s => ({ ...s, ready: true, hint: "Centra o corpo dentro da área" }));
      } catch (e) {
        const name = e?.name || "";
        let msg;
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          msg = "Permissão negada. Autoriza o acesso à câmara nas definições do navegador e tenta novamente.";
        } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          msg = "Nenhuma câmara encontrada neste dispositivo. Tenta carregar uma fotografia em alternativa.";
        } else if (name === "NotReadableError" || name === "TrackStartError") {
          msg = "A câmara está a ser usada por outra aplicação. Fecha-a e tenta de novo.";
        } else if (name === "OverconstrainedError") {
          msg = "Resolução não suportada — a tentar definições mais flexíveis…";
        } else if (location.protocol !== "https:" && location.hostname !== "localhost") {
          msg = "A câmara só funciona em ligações seguras (HTTPS). Recarrega via HTTPS.";
        } else {
          msg = e?.message || "Câmara indisponível.";
        }
        setState(s => ({ ...s, error: msg }));
      }
    }
    start();
    return () => {
      mounted = false;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track wrapper size to keep guide overlay in real container aspect ratio
  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      setWrapDims({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  const analyze = useCallback(() => {
    const v = videoRef.current;
    const c = analyzeCanvasRef.current;
    if (!v || !c || v.videoWidth === 0 || autoRef.current.capturing) return;

    const dw = 120, dh = 160;
    c.width = dw; c.height = dh;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, dw, dh);
    const data = ctx.getImageData(0, 0, dw, dh).data;

    const lum = new Float32Array(dw * dh);
    let sum = 0;
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      lum[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      sum += lum[p];
    }
    const meanLum = sum / lum.length;
    // Background estimate (corners)
    const bg = (lum[0] + lum[dw - 1] + lum[dw * (dh - 1)] + lum[dw * dh - 1]) / 4;
    const thr = 22;

    let minX = dw, maxX = 0, minY = dh, maxY = 0;
    let sumX = 0, sumY = 0, count = 0;
    let topX = 0, topC = 0, botX = 0, botC = 0;
    for (let y = 0; y < dh; y++) {
      for (let x = 0; x < dw; x++) {
        const idx = y * dw + x;
        if (Math.abs(lum[idx] - bg) > thr) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          sumX += x; sumY += y; count++;
          if (y < dh / 2) { topX += x; topC++; } else { botX += x; botC++; }
        }
      }
    }

    // Edge / sharpness heuristic
    let edgeSum = 0, edgeN = 0;
    for (let y = 2; y < dh - 2; y += 2) {
      for (let x = 2; x < dw - 2; x += 2) {
        const idx = y * dw + x;
        edgeSum += Math.abs(lum[idx] - lum[idx + 1]) + Math.abs(lum[idx] - lum[idx + dw]);
        edgeN++;
      }
    }
    const edgeMean = edgeSum / edgeN;

    const guidance = {
      centered: null, tilt: "ok", distance: "ok", lighting: "ok", sharpness: "ok",
    };
    const hints = [];

    if (meanLum < 55)      { guidance.lighting = "too-dark"; hints.push("Mais luz no ambiente, por favor"); }
    else if (meanLum > 215){ guidance.lighting = "too-bright"; hints.push("Luz excessiva — reduz o brilho"); }

    if (edgeMean < 2.2)    { guidance.sharpness = "blurry"; hints.push("Imagem desfocada — mantém-te imóvel"); }

    if (count > 250) {
      const bcx = sumX / count;
      const dx = (bcx - dw / 2) / dw;
      if (Math.abs(dx) > 0.08) {
        guidance.centered = false;
        hints.push(dx > 0 ? "Move-te ligeiramente para a tua esquerda" : "Move-te ligeiramente para a tua direita");
      } else guidance.centered = true;

      if (topC > 20 && botC > 20) {
        const tNorm = ((topX / topC) - (botX / botC)) / dw;
        if (Math.abs(tNorm) > 0.06) {
          guidance.tilt = tNorm > 0 ? "right" : "left";
          hints.push("Mantém os ombros alinhados");
        }
      }

      const bh = (maxY - minY) / dh;
      // Tolerância de distância — relaxada por defeito; modo "sala pequena"
      // aceita o paciente mais próximo (corpo maior no enquadramento).
      const distLow  = smallRoom ? 0.42 : 0.50;
      const distHigh = smallRoom ? 1.08 : 1.00;
      if (bh < distLow)        { guidance.distance = "too-far"; hints.push("Aproxima-te um passo"); }
      else if (bh > distHigh)  { guidance.distance = "too-close"; hints.push("Afasta-te ligeiramente"); }
    } else {
      hints.push("Centra o corpo dentro da área");
    }

    const severity = aggregateSeverity({ ...guidance, centered: guidance.centered !== false });
    const aligned = isAligned(guidance);
    const hint = aligned ? "Postura detetada — pronto para capturar" : (hints[0] || "Ajusta a posição");

    // Auto-capture
    if (state.auto && aligned) {
      autoRef.current.goodFrames += 1;
      if (autoRef.current.goodFrames >= 5) {
        autoRef.current.capturing = true;
        capture(true);
      }
    } else {
      autoRef.current.goodFrames = 0;
    }

    setState(s => ({ ...s, guidance, hint, severity: aligned ? "green" : severity }));
  }, [state.auto, smallRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!state.ready) return;
    const id = setInterval(analyze, 360);
    return () => clearInterval(id);
  }, [state.ready, analyze]);

  const capture = async (fromAuto = false) => {
    const v = videoRef.current;
    if (!v || v.videoWidth === 0) return;
    const canvas = document.createElement("canvas");
    const w = v.videoWidth, h = v.videoHeight;
    // Fit to max 1200px long edge
    const scale = Math.min(1, 1200 / Math.max(w, h));
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const url = canvas.toDataURL("image/jpeg", 0.88);

    // quick re-check of quality on the real frame
    const small = document.createElement("canvas");
    small.width = 120; small.height = 160;
    small.getContext("2d").drawImage(v, 0, 0, 120, 160);
    const d = small.getContext("2d").getImageData(0, 0, 120, 160).data;
    let s = 0; for (let i = 0; i < d.length; i += 4) s += d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114;
    const mean = s / (120 * 160);
    if (mean < 40 || mean > 225) {
      autoRef.current.capturing = false;
      toast.error("Qualidade de imagem insuficiente, volta a tentar", { description: "Verifica a luminosidade" });
      return;
    }
    if (fromAuto) toast.success("Captura automática concluída");
    onCapture(url);
  };

  const toggleAuto = () => {
    autoRef.current.goodFrames = 0;
    setState(s => ({ ...s, auto: !s.auto }));
  };

  const close = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    onCancel?.();
  };

  // Overlay colors by severity
  const tok = NEON[state.severity] || NEON.blue;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col" data-testid="camera-capture">
      {/* top bar */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <Camera className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Captura guiada</div>
            <div className="font-display text-base text-slate-50">
              {view === "front" ? "Vista frontal" : view === "side" ? "Vista lateral" : "Vista posterior"}
            </div>
          </div>
        </div>
        <Button variant="ghost" onClick={close} className="text-slate-300 hover:text-slate-50" data-testid="camera-close">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* viewport */}
      <div ref={wrapRef} className="relative flex-1 overflow-hidden bg-black">
        {state.error ? (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="max-w-md text-center rounded-2xl border border-rose-500/30 bg-rose-500/5 p-8">
              <AlertTriangle className="w-8 h-8 mx-auto text-rose-400" strokeWidth={1.5} />
              <div className="font-display text-xl text-slate-50 mt-3">Câmara indisponível</div>
              <div className="text-slate-400 text-sm mt-2">{state.error}</div>
              <div className="text-slate-500 text-xs mt-3">Concede permissão à câmara ou usa "Carregar foto".</div>
            </div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              muted
              data-testid="camera-video"
            />
            <GuideOverlay view={view} token={tok} guidance={state.guidance} gender={gender} dims={wrapDims} compact={smallRoom} />
          </>
        )}

        {/* hint strip */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-slate-950/80 backdrop-blur border border-slate-800 text-sm text-slate-100 flex items-center gap-2" data-testid="camera-hint">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: tok.stroke, boxShadow: `0 0 10px ${tok.glow}` }}
          />
          {state.hint}
        </div>

        {/* room-size toggle (top-right) */}
        <button
          type="button"
          onClick={() => setSmallRoom(s => !s)}
          data-testid="camera-room-toggle"
          aria-pressed={smallRoom}
          className={`absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] backdrop-blur border transition ${
            smallRoom
              ? "bg-emerald-500/15 border-emerald-500/45 text-emerald-200"
              : "bg-slate-950/75 border-slate-700 text-slate-300 hover:text-slate-100"
          }`}
          title={smallRoom ? "Modo sala pequena (distância curta)" : "Modo corpo inteiro (distância padrão)"}
        >
          {smallRoom ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          {smallRoom ? "Sala pequena" : "Corpo inteiro"}
        </button>

        {/* aligned badge */}
        {state.severity === "green" && (
          <div className="absolute bottom-28 left-1/2 -translate-x-1/2 flex items-center gap-2 text-emerald-300 text-sm px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 glow-emerald">
            <CheckCircle2 className="w-4 h-4" /> Posição ideal
          </div>
        )}
      </div>

      {/* bottom controls */}
      <div className="px-4 md:px-6 py-5 border-t border-slate-800/80 bg-slate-950/90 backdrop-blur-xl flex items-center justify-between gap-4">
        <Button
          variant="outline"
          onClick={toggleAuto}
          className={`rounded-full border-slate-700 ${state.auto ? "text-emerald-300 border-emerald-500/40" : "text-slate-300"}`}
          data-testid="camera-auto-toggle"
        >
          {state.auto ? <Zap className="w-4 h-4 mr-2" /> : <ZapOff className="w-4 h-4 mr-2" />}
          {state.auto ? "Auto-captura ON" : "Auto-captura"}
        </Button>

        <button
          type="button"
          onClick={() => capture(false)}
          disabled={!state.ready || !!state.error}
          data-testid="camera-shutter"
          className="relative w-20 h-20 rounded-full border-4 border-slate-800 bg-slate-950 transition-all"
          style={{ boxShadow: state.severity === "green" ? `0 0 30px ${tok.glow}, inset 0 0 0 2px ${tok.stroke}` : undefined }}
        >
          <span className="absolute inset-2 rounded-full" style={{ background: tok.stroke }} />
        </button>

        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Qualidade</div>
          <div className="flex gap-1.5 mt-1 justify-end">
            <QualityDot label="Luz" ok={state.guidance.lighting === "ok"} />
            <QualityDot label="Foco" ok={state.guidance.sharpness === "ok"} />
            <QualityDot label="Centro" ok={state.guidance.centered === true} />
            <QualityDot label="Dist." ok={state.guidance.distance === "ok"} />
          </div>
        </div>
      </div>

      <canvas ref={analyzeCanvasRef} className="hidden" />
    </div>
  );
}

function QualityDot({ label, ok }) {
  return (
    <div className="flex flex-col items-center text-[9px] text-slate-500 font-mono">
      <span
        className="w-2 h-2 rounded-full"
        style={{
          background: ok ? "rgba(52,211,153,0.9)" : "rgba(244,63,94,0.9)",
          boxShadow: ok ? "0 0 8px rgba(52,211,153,0.7)" : "0 0 8px rgba(244,63,94,0.7)",
        }}
      />
      {label}
    </div>
  );
}

/* ----------------------- Overlay (silhouette + guides) ----------------------- */
/**
 * Calibração da moldura de captura.
 *
 * Bug anterior: viewBox 100×100 com `xMidYMid meet` em câmara 9:16 portrait
 * fazia a silhueta ocupar apenas ~50% da altura visível (encaixada ao centro
 * num quadrado), forçando o paciente a recuar muito para "caber" no contorno.
 *
 * Correção: viewBox dinâmico que respeita o aspect-ratio real do contentor,
 * silhueta paramétrica em coordenadas normalizadas [0,1] da altura do frame,
 * com modo "sala pequena" que aproxima cabeça/pés das bordas e permite
 * distância clínica curta. Apenas overlay UI — não afeta MediaPipe nem
 * a imagem capturada.
 */
function GuideOverlay({ view, token, guidance, gender = "neutro", dims = { w: 0, h: 0 }, compact = false }) {
  // Color individual guides based on heuristics
  const centerColor = guidance.centered === true ? NEON.green.stroke : guidance.centered === false ? NEON.red.stroke : NEON.blue.stroke;
  const tiltColor   = guidance.tilt === "ok" ? NEON.green.stroke : NEON.amber.stroke;
  const distColor   = guidance.distance === "ok" ? NEON.green.stroke : NEON.amber.stroke;

  // viewBox aspect = container aspect, so 1 vbX = 1 vbY na renderização final
  const VB_W = 100;
  const aspect = dims.w > 0 && dims.h > 0 ? dims.w / dims.h : 9 / 16;
  const VB_H  = +(VB_W / Math.max(0.3, aspect)).toFixed(2);

  // Pontos anatómicos como % da altura do viewBox (coerentes para todas as vistas).
  // Default = corpo inteiro com margem segura ~8% topo/base.
  // Compact = sala pequena, paciente mais próximo, margens ~4% topo/base.
  const m = compact
    ? { headTop: 0.04, headBot: 0.13, shoulder: 0.17, chest: 0.27, waist: 0.46, hip: 0.58, thigh: 0.74, foot: 0.96 }
    : { headTop: 0.08, headBot: 0.20, shoulder: 0.24, chest: 0.34, waist: 0.50, hip: 0.62, thigh: 0.74, foot: 0.92 };
  const Y = (n) => +(n * VB_H).toFixed(2);

  // Larguras antropométricas (em unidades VB_W=100, mantém-se em % da largura).
  const isF = gender === "feminino";
  const isM = gender === "masculino";
  const sL = isM ? 38 : 40, sR = isM ? 62 : 60;
  const hL = isF ? 38 : 40, hR = isF ? 62 : 60;
  const wL = isF ? 42 : 41, wR = isF ? 58 : 59;

  let path;
  if (view === "side") {
    // Perfil sagital — coluna ligeiramente em S, braços ocultos atrás
    path = `
      M 50,${Y(m.headTop)} Q 45,${Y(m.headTop)} 45,${Y((m.headTop + m.headBot) / 2)} Q 45,${Y(m.headBot)} 50,${Y(m.headBot)} Q 55,${Y(m.headBot)} 55,${Y((m.headTop + m.headBot) / 2)} Q 55,${Y(m.headTop)} 50,${Y(m.headTop)} Z
      M 46,${Y(m.shoulder)} L 56,${Y(m.shoulder)} L 56,${Y(m.chest)} Q 60,${Y(m.chest + 0.04)} 62,${Y(m.waist - 0.02)} Q 63,${Y(m.waist + 0.04)} 58,${Y(m.waist + 0.08)} L 56,${Y(m.hip + 0.06)} L 55,${Y(m.thigh)} L 52,${Y(m.foot)} L 49,${Y(m.foot)} L 48,${Y(m.thigh)} L 47,${Y(m.hip + 0.06)} L 47,${Y(m.waist + 0.08)} Q 44,${Y(m.waist + 0.02)} 46,${Y(m.chest + 0.04)} Z
    `;
  } else {
    // Frontal e posterior partilham silhueta (vista anterior/posterior do tronco coerentes)
    path = `
      M 50,${Y(m.headTop)} Q 44,${Y(m.headTop)} 44,${Y((m.headTop + m.headBot) / 2)} Q 44,${Y(m.headBot)} 50,${Y(m.headBot)} Q 56,${Y(m.headBot)} 56,${Y((m.headTop + m.headBot) / 2)} Q 56,${Y(m.headTop)} 50,${Y(m.headTop)} Z
      M ${sL},${Y(m.shoulder)} L ${sR},${Y(m.shoulder)} L 63,${Y(m.chest)} L ${wR + 4},${Y(m.waist)} Q ${wR + 2},${Y(m.waist + 0.02)} ${wR - 2},${Y(m.waist + 0.01)} L ${hR - 2},${Y(m.hip)} L 56,${Y(m.thigh + 0.10)} L 52,${Y(m.foot)} L 48,${Y(m.foot)} L 44,${Y(m.thigh + 0.10)} L ${hL + 2},${Y(m.hip)} L ${wL + 2},${Y(m.waist + 0.01)} Q ${wL - 2},${Y(m.waist + 0.02)} ${wL - 4},${Y(m.waist)} L 37,${Y(m.chest)} Z
    `;
  }

  // Guias horizontais alinhadas à silhueta atual
  const guides = [
    { y: Y(m.shoulder), label: "Ombros", col: tiltColor },
    { y: Y(m.hip),      label: "Bacia",  col: distColor },
    { y: Y(m.thigh + 0.06), label: "Joelhos", col: distColor },
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      style={{ mixBlendMode: "screen" }}
      data-testid="camera-overlay"
    >
      <defs>
        <filter id="cam-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="0.6" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="cam-plumb" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={centerColor} stopOpacity="0" />
          <stop offset="18%" stopColor={centerColor} stopOpacity="0.55" />
          <stop offset="82%" stopColor={centerColor} stopOpacity="0.55" />
          <stop offset="100%" stopColor={centerColor} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* moldura externa segura */}
      <rect x="3" y={Y(0.025)} width={VB_W - 6} height={Y(0.95) - Y(0.025)} rx="3" fill="none"
            stroke={token.stroke} strokeOpacity="0.22" strokeWidth="0.2"
            strokeDasharray="1 1.5" />

      {/* silhueta — outer halo + crisp outline */}
      <path d={path}
            fill="none" stroke={token.stroke} strokeOpacity="0.28"
            strokeWidth="1.4" strokeLinejoin="round" filter="url(#cam-glow)" />
      <path d={path}
            fill="none" stroke={token.stroke} strokeOpacity="0.9"
            strokeWidth="0.4" strokeLinejoin="round" />

      {/* plumb beam (vertical, full-height do viewBox dinâmico) */}
      <rect x="48.6" y="0" width="2.8" height={VB_H} fill="url(#cam-plumb)" opacity="0.5" filter="url(#cam-glow)" />
      <line x1="50" y1="0" x2="50" y2={VB_H}
            stroke={centerColor} strokeOpacity="0.75" strokeWidth="0.18" strokeDasharray="0.6 1.4" />

      {/* horizontal guides — alinhadas à silhueta no modo ativo */}
      {guides.map((g, i) => (
        <g key={i}>
          <line x1="18" y1={g.y} x2="82" y2={g.y}
                stroke={g.col} strokeOpacity="0.5" strokeWidth="0.9"
                filter="url(#cam-glow)" />
          <line x1="18" y1={g.y} x2="82" y2={g.y}
                stroke={g.col} strokeOpacity="0.95" strokeWidth="0.2" />
          <text x="83" y={g.y + 1.4} fill={g.col} fillOpacity="0.85"
                fontSize="2.1" fontFamily="monospace" style={{ letterSpacing: "0.05em" }}>
            {g.label}
          </text>
        </g>
      ))}

      {/* corner ticks — encostados às margens do viewBox dinâmico */}
      <g stroke={token.stroke} strokeOpacity="0.6" strokeWidth="0.25" fill="none">
        <path d={`M 3 ${Y(0.05)} L 3 ${Y(0.025)} L 8 ${Y(0.025)}`} />
        <path d={`M ${VB_W - 3} ${Y(0.05)} L ${VB_W - 3} ${Y(0.025)} L ${VB_W - 8} ${Y(0.025)}`} />
        <path d={`M 3 ${Y(0.92)} L 3 ${Y(0.95)} L 8 ${Y(0.95)}`} />
        <path d={`M ${VB_W - 3} ${Y(0.92)} L ${VB_W - 3} ${Y(0.95)} L ${VB_W - 8} ${Y(0.95)}`} />
      </g>
    </svg>
  );
}
